/**
 * Fractional index keys: a question's place in a form is a short string, and
 * the form is whatever you get by sorting on it.
 *
 * A reorder therefore writes one key on one question. That matters because the
 * draft is a CRDT: a list has no "move" operation, so reordering a list is a
 * delete plus an insert, and two people dragging the same question at once end
 * up with two copies of it that no later merge can reconcile. Two writes to one
 * key resolve to one winner instead.
 *
 * Keys are base-62 fractions with an implied leading "0.", and `DIGITS` is in
 * ASCII order, so plain string comparison sorts them correctly.
 */

const DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

/** Whether every digit belongs to the ordering alphabet. */
export function isBase62SortKey(key: string): boolean {
  return key.length > 0 && [...key].every((digit) => DIGITS.includes(digit));
}

/**
 * A short random suffix, so two clients generating a key in the same gap don't
 * generate the *same* key.
 *
 * `keyBetween` is a pure function of its neighbours, so two admins adding a
 * question at the same spot would otherwise compute identical keys — and once
 * merged, dropping anything between those two items asks for a key strictly
 * between a value and itself, which cannot exist. The suffix only ever extends
 * a key, and an extended key still sorts after `prev` and before `next`
 * (`keyBetween` never returns a prefix of `next`), so ordering is unaffected.
 */
function jitter(): string {
  let out = "";

  for (let i = 0; i < 4; i++) {
    out += DIGITS[10 + Math.floor(Math.random() * 52)]!;
  }

  return out;
}

/**
 * A key strictly between `prev` and `next`.
 *
 * Pass `null` for `prev` to mean "before everything" and `null` for `next` to
 * mean "after everything"; both null yields a key for an empty list.
 *
 * Terminates for any valid input: once the index passes the end of one side,
 * that digit pins to 0 or 62 and always leaves room. Repeatedly splitting the
 * same gap grows the key by one character each time, which is the cost every
 * fractional-index scheme pays.
 */
export function keyBetween(
  prev: string | null | undefined,
  next: string | null | undefined,
  { unique = true }: { unique?: boolean } = {},
): string {
  const p = prev ?? "";
  const n = next ?? "";

  if (p && n && p >= n) {
    throw new Error(`keyBetween called with prev >= next: ${p} >= ${n}`);
  }

  // A key of nothing but zeros is the zero fraction, and nothing sorts before
  // it — appending digits to "0" only ever produces something larger. Keys are
  // generated so this can't arise (see `initialKeys`); fail loudly rather than
  // return a key that sorts on the wrong side.
  if (n !== "" && /^0+$/.test(n)) {
    throw new Error(`no key exists before the minimum key: ${n}`);
  }

  let out = "";

  // Bounded only as a runaway guard; the loop returns long before this.
  for (let i = 0; i < 4096; i++) {
    const pc = i < p.length ? DIGITS.indexOf(p[i]!) : 0;
    const nc = i < n.length ? DIGITS.indexOf(n[i]!) : DIGITS.length;

    if (pc === -1 || nc === -1) {
      throw new Error(`keyBetween called with a non-base-62 key: ${p}, ${n}`);
    }

    if (pc + 1 < nc) {
      const key = out + DIGITS[Math.floor((pc + nc) / 2)]!;
      return unique ? key + jitter() : key;
    }

    out += DIGITS[pc]!;
  }

  /* c8 ignore next */
  throw new Error("keyBetween failed to converge");
}

/** Evenly spread starting keys, for seeding a form that has no order yet. */
export function initialKeys(count: number): string[] {
  const keys: string[] = [];

  for (let i = 0; i < count; i++) {
    const prev = keys.length > 0 ? keys[keys.length - 1]! : null;
    // Spread across the range rather than repeatedly splitting the tail, so
    // keys stay one character for reasonably sized forms. Past 62 questions
    // the evenly spread slots start colliding, so fall back to subdividing.
    //
    // Never slot 0: an all-zero key is the bottom of the range, so nothing can
    // be inserted before it and dragging to the front would silently land
    // second. Keeping digit 0 free preserves room at both ends.
    const slot =
      DIGITS[Math.max(1, Math.floor(((i + 1) * DIGITS.length) / (count + 1)))];
    keys.push(
      slot !== undefined && (prev === null || slot > prev)
        ? slot
        : keyBetween(prev, null, { unique: false }),
    );
  }

  return keys;
}

/** Sort comparator for anything carrying a `pos`, with `id` as a stable tiebreak. */
export function byPos<T extends { id: string; pos: string }>(
  a: T,
  b: T,
): number {
  if (a.pos !== b.pos) {
    return a.pos < b.pos ? -1 : 1;
  }

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The key for dropping `movedId` at `toIndex` in `ordered` — the list as the
 * dragger currently sees it, including the question being moved.
 */
export function keyForDrop<T extends { id: string; pos: string }>(
  ordered: readonly T[],
  movedId: string,
  toIndex: number,
): string {
  const others = ordered.filter((item) => item.id !== movedId);
  const clamped = Math.max(0, Math.min(toIndex, others.length));
  const prev = others[clamped - 1]?.pos ?? null;
  const next = others[clamped]?.pos ?? null;

  // Two items can still share a key — from a document written before keys
  // carried a suffix, or from a merge of two older clients. No key exists
  // between a value and itself, so bound the search by the next key that is
  // genuinely larger. Using no upper bound would fling the question past
  // everything after it instead of leaving it in the gap it was dropped into.
  if (prev !== null && next !== null && prev >= next) {
    const above = others.slice(clamped).find((item) => item.pos > prev);
    return keyBetween(prev, above?.pos ?? null);
  }

  return keyBetween(prev, next);
}
